import { useEffect, useRef } from "react";

const maxDuration = 250;

export function useQuickSwipeUp(
  onQuickSwipeUp: () => void,
  isQuickSwipeUpModalEnabled: boolean,
  isModalOpened: boolean
) {
  const stateRef = useRef<{
    startY: number;
    startTime: number;
    isTracking: boolean;
    lastMoveY: number;
    lastMoveTime: number;
  }>({
    startY: 0,
    startTime: 0,
    isTracking: false,
    lastMoveY: 0,
    lastMoveTime: 0,
  });
  // Latest-ref so callers can pass inline arrows without re-binding listeners
  // on every parent re-render.
  const onQuickSwipeUpRef = useRef(onQuickSwipeUp);
  onQuickSwipeUpRef.current = onQuickSwipeUp;

  useEffect(() => {
    if (!isQuickSwipeUpModalEnabled || isModalOpened) return;
    const getIsQuickSwipeUp = (distance: number, duration: number, minDistance: number) =>
      Math.sign(distance) === -1 && Math.abs(distance) > minDistance && duration < maxDuration;

    const resetTracking = () => {
      const s = stateRef.current;
      s.isTracking = false;
      s.lastMoveY = 0;
      s.lastMoveTime = 0;
    };

    const finishGesture = (endY: number, minDistance: number) => {
      const s = stateRef.current;
      const distance = endY - s.startY;
      const duration = Date.now() - s.startTime;

      if (getIsQuickSwipeUp(distance, duration, minDistance)) {
        onQuickSwipeUpRef.current();
      }
      resetTracking();
    };

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      const s = stateRef.current;
      s.startY = touch.clientY;
      s.startTime = Date.now();
      s.isTracking = true;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      const s = stateRef.current;
      if (!s.isTracking) return;
      const touch = e.changedTouches[0];
      finishGesture(touch.clientY, 100);
    };

    const handleMouseDown = (e: MouseEvent) => {
      const s = stateRef.current;
      s.startY = e.clientY;
      s.startTime = Date.now();
      s.isTracking = true;
      s.lastMoveY = 0;
      s.lastMoveTime = 0;
    };

    const handleMouseUp = (e: MouseEvent) => {
      const s = stateRef.current;
      if (!s.isTracking) return;
      finishGesture(e.clientY, 150);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const minDistance = 50;
      const s = stateRef.current;

      if (!s.isTracking) {
        return;
      }

      const now = Date.now();
      if (s.lastMoveTime === 0) {
        s.lastMoveY = e.movementY;
        s.lastMoveTime = now;
        return;
      }
      const distance = e.movementY - s.lastMoveY;
      const duration = now - s.lastMoveTime;

      if (Math.sign(e.movementY) === -1 && Math.abs(distance) > minDistance && duration < maxDuration) {
        onQuickSwipeUpRef.current();
      }

      s.lastMoveY = e.movementY;
      s.lastMoveTime = now;
    };

    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [isQuickSwipeUpModalEnabled, isModalOpened]);
}
